import { useCallback, useEffect, useState } from 'react';
import { Rabbit, RefreshCw, Trash2, Wifi, Database, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/ui/status-badge';
import { Modal } from '../components/ui/modal';
import { RabbitMQOverview, RabbitMQQueue, RabbitMQConnection } from '../models';
import { getRabbitMQOverview, getRabbitMQQueues, getRabbitMQConnections, purgeRabbitMQQueue, deleteRabbitMQQueue, getApiError } from '../services/api';

export const RabbitMQPage = () => {
  const [overview, setOverview] = useState<RabbitMQOverview>();
  const [queues, setQueues] = useState<RabbitMQQueue[]>();
  const [connections, setConnections] = useState<RabbitMQConnection[]>();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmAction, setConfirmAction] = useState<{ type: 'purge' | 'delete'; queue: string }>();

  const load = useCallback(async () => {
    setError('');
    try {
      const [ov, qu, conn] = await Promise.all([
        getRabbitMQOverview().catch(() => null),
        getRabbitMQQueues().catch(() => null),
        getRabbitMQConnections().catch(() => null),
      ]);
      if (ov) setOverview(ov.data.data);
      if (qu) setQueues(qu.data.data);
      if (conn) setConnections(conn.data.data);
    } catch (e) {
      setError(getApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePurge = async () => {
    if (!confirmAction || confirmAction.type !== 'purge') return;
    setError(''); setSuccess('');
    try {
      await purgeRabbitMQQueue(confirmAction.queue);
      setSuccess(`Очередь "${confirmAction.queue}" очищена`);
      setConfirmAction(undefined);
      load();
    } catch (e) { setError(getApiError(e)); }
  };

  const handleDelete = async () => {
    if (!confirmAction || confirmAction.type !== 'delete') return;
    setError(''); setSuccess('');
    try {
      await deleteRabbitMQQueue(confirmAction.queue);
      setSuccess(`Очередь "${confirmAction.queue}" удалена`);
      setConfirmAction(undefined);
      load();
    } catch (e) { setError(getApiError(e)); }
  };

  return (
    <AppLayout title="RabbitMQ">
      <PageHeader
        title="RabbitMQ Management"
        description="Очереди, обмены, соединения и управление сообщениями."
        action={<Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /> Обновить</Button>}
      />
      {error && <div className="mb-4 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {success && <div className="mb-4 rounded-xl border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">{success}</div>}

      {!overview && !error ? <LoadingState /> : overview && (
        <div className="space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: 'Очереди', value: overview.objectTotals?.queues || 0, icon: Database },
              { label: 'Соединения', value: overview.objectTotals?.connections || 0, icon: Wifi },
              { label: 'Сообщений', value: overview.queueTotals?.messages || 0, icon: ArrowUpRight },
              { label: 'RabbitMQ', value: overview.rabbitVersion || '—', icon: Rabbit },
            ].map((stat) => (
              <div key={stat.label} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2">
                  <stat.icon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{stat.label}</span>
                </div>
                <p className="mt-2 text-xl font-bold text-foreground">{stat.value}</p>
              </div>
            ))}
          </div>

          {/* Queues */}
          <div className="rounded-2xl border border-border bg-card shadow-sm">
            <div className="border-b border-border p-4">
              <h2 className="text-sm font-bold">Очереди</h2>
            </div>
            {!queues ? <LoadingState /> : queues.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Очереди не найдены</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-4 py-3">Имя</th>
                      <th className="px-4 py-3 text-center">Сообщений</th>
                      <th className="px-4 py-3 text-center">Потребителей</th>
                      <th className="px-4 py-3">Состояние</th>
                      <th className="px-4 py-3 text-right">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queues.map((q) => (
                      <tr key={q.name} className="border-b border-border/60 last:border-0 hover:bg-muted/40">
                        <td className="px-4 py-3 font-mono text-xs">{q.name}</td>
                        <td className="px-4 py-3 text-center font-mono">{q.messages}</td>
                        <td className="px-4 py-3 text-center font-mono">{q.consumers}</td>
                        <td className="px-4 py-3">
                          <StatusBadge label={q.state} variant={q.state === 'running' ? 'success' : 'default'} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => setConfirmAction({ type: 'purge', queue: q.name })} title="Очистить" className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-warning/10 hover:text-warning">
                              <RefreshCw className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setConfirmAction({ type: 'delete', queue: q.name })} title="Удалить" className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Connections */}
          <div className="rounded-2xl border border-border bg-card shadow-sm">
            <div className="border-b border-border p-4">
              <h2 className="text-sm font-bold">Соединения</h2>
            </div>
            {!connections ? <LoadingState /> : connections.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Нет активных соединений</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-4 py-3">Пользователь</th>
                      <th className="px-4 py-3">Хост</th>
                      <th className="px-4 py-3 text-center">Каналы</th>
                      <th className="px-4 py-3">Состояние</th>
                    </tr>
                  </thead>
                  <tbody>
                    {connections.map((c, i) => (
                      <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/40">
                        <td className="px-4 py-3 font-medium">{c.user}</td>
                        <td className="px-4 py-3 font-mono text-xs">{c.peerHost}:{c.peerPort}</td>
                        <td className="px-4 py-3 text-center font-mono">{c.channels}</td>
                        <td className="px-4 py-3">
                          <StatusBadge label={c.state} variant={c.state === 'running' ? 'success' : 'default'} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal open={Boolean(confirmAction)} onClose={() => setConfirmAction(undefined)}
        title={confirmAction?.type === 'purge' ? 'Очистить очередь' : 'Удалить очередь'}
        maxWidth="max-w-md"
        footer={<>
          <Button variant="ghost" onClick={() => setConfirmAction(undefined)}>Отмена</Button>
          <Button variant={confirmAction?.type === 'delete' ? 'destructive' : 'default'}
            onClick={confirmAction?.type === 'purge' ? handlePurge : handleDelete}>
            {confirmAction?.type === 'purge' ? 'Очистить' : 'Удалить'}
          </Button>
        </>}
      >
        <div className="flex gap-3 rounded-xl border border-warning/25 bg-warning/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <p>
            {confirmAction?.type === 'purge'
              ? <>Все сообщения в очереди <strong>"{confirmAction?.queue}"</strong> будут безвозвратно удалены.</>
              : <>Очередь <strong>"{confirmAction?.queue}"</strong> будет удалена. Это действие необратимо.</>
            }
          </p>
        </div>
      </Modal>
    </AppLayout>
  );
};
